import React, { useEffect, useState } from "react";
import { UserPlus, Copy, Check, Trash2 } from "lucide-react";
import { api } from "@/lib/api";

const ROLE_CLASS = { owner: "b-prod", admin: "b-wait", operator: "b-muted", viewer: "b-muted" };

/**
 * The workspace's people.
 *
 * Until this existed a client could not add a colleague at all — memberships
 * were created by hand in SQL or by Prioris staff, so every new hire was a
 * support request.
 */
export default function SettingsMembers() {
  const [data, setData] = useState(null);
  const [roles, setRoles] = useState([]);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ email: "", name: "", role: "operator" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [invited, setInvited] = useState(null);
  const [copied, setCopied] = useState(false);

  const load = () => api.get("/members").then((r) => setData(r.data)).catch(() => setData(false));
  useEffect(() => {
    load();
    api.get("/members/roles").then((r) => setRoles(r.data.roles || [])).catch(() => {});
  }, []);

  const invite = async (e) => {
    e?.preventDefault();
    setBusy(true); setError("");
    try {
      const { data: created } = await api.post("/members", draft);
      setInvited(created);
      setAdding(false);
      setDraft({ email: "", name: "", role: "operator" });
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || "Could not add this person");
    } finally {
      setBusy(false);
    }
  };

  const setRole = async (m, role) => {
    setError("");
    try {
      await api.patch(`/members/${m.user_id}`, { role });
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || "Could not change that role");
    }
  };

  const revoke = async (m) => {
    if (!window.confirm(
      `Remove ${m.email}?\n\n` +
      "They lose access on their next request. What they already did stays on " +
      "the record, and their Prioris login itself is untouched."
    )) return;
    setError("");
    try {
      await api.delete(`/members/${m.user_id}`);
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || "Could not remove this person");
    }
  };

  if (data === null) return <div className="text-[13px]" style={{ color: "var(--text-muted)" }}>Loading people…</div>;
  if (data === false) return <div className="text-[13px]" style={{ color: "var(--text-muted)" }}>Could not load people.</div>;

  const { used, limit } = data.seats || {};
  const full = limit != null && used >= limit;

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div className="text-[12.5px]" style={{ color: "var(--text-secondary)" }} data-testid="seat-count">
          {used} {limit == null ? "people" : `of ${limit} seats used`}
          {full && <span className="badge b-sla ml-2">Plan limit reached</span>}
        </div>
        <button className="btn" data-testid="add-member" disabled={full}
                onClick={() => { setAdding((v) => !v); setInvited(null); }}>
          <UserPlus size={13} /> Add person
        </button>
      </div>

      {invited && (
        <div className="card p-4 mb-4" data-testid="invite-result">
          <div className="text-[12.5px] mb-2">
            <span className="font-medium">{invited.email}</span> can now sign in.
          </div>
          {invited.password ? (
            <button className="btn btn-ghost font-mono text-[12px]" data-testid="copy-password"
                    onClick={() => { navigator.clipboard?.writeText(invited.password);
                                     setCopied(true); setTimeout(() => setCopied(false), 2000); }}>
              {copied ? <Check size={12} /> : <Copy size={12} />} {invited.password}
            </button>
          ) : (
            // Honest rather than convenient: no new password was set, so there
            // is none to hand over.
            <div className="text-[12px]" style={{ color: "var(--text-muted)" }}>
              They already had a Prioris login, so they keep the password they use.
            </div>
          )}
          <div className="text-[11px] mt-2" style={{ color: "var(--text-muted)" }}>
            Shown once. It cannot be retrieved again.
          </div>
        </div>
      )}

      {adding && (
        <form className="card p-4 mb-4" onSubmit={invite} data-testid="invite-form">
          <div className="grid grid-cols-12 gap-2">
            <input className="input col-span-12 md:col-span-5" type="email" required placeholder="email@company.com"
                   data-testid="invite-email" value={draft.email}
                   onChange={(e) => setDraft({ ...draft, email: e.target.value })} />
            <input className="input col-span-12 md:col-span-4" required placeholder="Full name"
                   data-testid="invite-name" value={draft.name}
                   onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
            <select className="input col-span-12 md:col-span-3" data-testid="invite-role" value={draft.role}
                    onChange={(e) => setDraft({ ...draft, role: e.target.value })}>
              {roles.filter((r) => r.grantable).map((r) => (
                <option key={r.role} value={r.role}>{r.role}</option>
              ))}
            </select>
          </div>
          {/* The role picker explains itself from the server's own permission
              map, so what it says cannot drift from what the role does. */}
          <p className="text-[11.5px] mt-2" style={{ color: "var(--text-muted)" }}>
            {roles.find((r) => r.role === draft.role)?.summary}
          </p>
          <div className="flex gap-2 mt-3">
            <button className="btn" type="submit" disabled={busy} data-testid="save-member">
              {busy ? "Adding…" : "Add person"}
            </button>
            <button className="btn btn-ghost" type="button" onClick={() => setAdding(false)}>Cancel</button>
          </div>
        </form>
      )}

      {error && <div className="badge b-rev mb-3 inline-block" data-testid="members-error">{error}</div>}

      <div className="card overflow-x-auto" data-testid="members-table">
        <table className="w-full min-w-[640px] text-[13px]">
          <thead>
            <tr className="text-left" style={{ color: "var(--text-muted)", borderBottom: "1px solid var(--border)" }}>
              <th className="py-2 px-3 font-medium text-[11px] uppercase tracking-wider">Email</th>
              <th className="py-2 px-3 font-medium text-[11px] uppercase tracking-wider">Name</th>
              <th className="py-2 px-3 font-medium text-[11px] uppercase tracking-wider">Role</th>
              <th className="py-2 px-3 font-medium text-[11px] uppercase tracking-wider">Status</th>
              <th className="py-2 px-3" />
            </tr>
          </thead>
          <tbody>
            {data.members.map((m) => (
              <tr key={m.user_id} style={{ borderBottom: "1px solid var(--border)" }}
                  data-testid={`member-${m.email}`}>
                <td className="py-2.5 px-3">
                  {m.email}
                  {m.is_self && <span className="badge b-muted ml-2">you</span>}
                </td>
                <td className="py-2.5 px-3">{m.name}</td>
                <td className="py-2.5 px-3">
                  {m.status === "active" ? (
                    <select className="input" style={{ padding: "3px 8px", fontSize: 12, width: "auto" }}
                            value={m.role} data-testid={`role-${m.email}`}
                            onChange={(e) => setRole(m, e.target.value)}>
                      {roles.filter((r) => r.grantable || r.role === m.role).map((r) => (
                        <option key={r.role} value={r.role}>{r.role}</option>
                      ))}
                    </select>
                  ) : (
                    <span className={`badge ${ROLE_CLASS[m.role] || "b-muted"}`}>{m.role}</span>
                  )}
                </td>
                <td className="py-2.5 px-3">
                  <span className={`badge ${m.status === "active" ? "b-healthy" : "b-muted"}`}>
                    {m.status}
                  </span>
                </td>
                <td className="py-2.5 px-3 text-right">
                  {m.status === "active" && !m.is_self && (
                    <button className="btn btn-ghost" style={{ padding: "3px 9px", fontSize: 11 }}
                            data-testid={`revoke-${m.email}`} onClick={() => revoke(m)}>
                      <Trash2 size={11} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
